import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ArrowLeft, Phone, Mail, Eye, EyeOff, Lock } from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

interface AuthScreenProps {
  initialMfaMode?: boolean;
}

const AuthScreen = ({ initialMfaMode = false }: AuthScreenProps = {}) => {
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [loginMethod, setLoginMethod] = useState<"email" | "phone">("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [fullName, setFullName] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Custom 2FA (MFA) State
  const [showMFAChallenge, setShowMFAChallenge] = useState(initialMfaMode);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    if (initialMfaMode) {
      supabase.auth.mfa.listFactors().then(({ data }) => {
        if (data && data.totp.length > 0) {
          setMfaFactorId(data.totp[0].id);
        }
      });
    }
  }, [initialMfaMode]);

  const calculatePasswordStrength = (pwd: string) => {
    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (/[a-zA-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^a-zA-Z0-9]/.test(pwd)) score += 1;

    if (pwd.length < 6) return { score: 0, label: "Very Weak", color: "bg-destructive/30" };
    if (score < 2) return { score: 1, label: "Weak", color: "bg-destructive" };
    if (score === 2) return { score: 2, label: "Fair", color: "bg-amber-500" };
    if (score >= 3) return { score: 3, label: "Strong", color: "bg-success" };
    return { score: 0, label: "Weak", color: "bg-destructive" };
  };

  const pwdStrength = calculatePasswordStrength(password);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (val.startsWith("0")) {
      val = "+63" + val.slice(1);
    } else if (val.startsWith("9") && val.length === 1) {
      val = "+63" + val;
    }
    val = val.replace(/[^\d+]/g, "");
    setPhone(val);
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 800));

      setShowOtpInput(true);
      toast({
        title: "OTP Sent! 📩",
        description: `SMS is not live. Please use dummy code '123456'.`
      });
    } catch (error: unknown) {
      toast({
        title: "Failed to send OTP",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 800));

      if (otp === "123456") {
        toast({ title: "Verified! ✅", description: "Welcome to Serviz Hub!" });
        toast({ title: "Login Simulation", description: "In production, this logs you in. Use Email for now!", variant: "default" });
      } else {
        throw new Error("Invalid OTP dummy code. Use 123456");
      }
    } catch (error: unknown) {
      toast({
        title: "Verification failed",
        description: error instanceof Error ? error.message : "Invalid code. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (mode === "signup") {
      if (password !== confirmPassword) {
        toast({ title: "Passwords don't match", variant: "destructive" });
        setLoading(false);
        return;
      }
      if (pwdStrength.score < 2) {
        toast({ title: "Password is too weak", description: "Please use at least 8 characters with letters and numbers.", variant: "destructive" });
        setLoading(false);
        return;
      }

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: fullName },
          emailRedirectTo: window.location.origin,
        },
      });
      if (error) {
        toast({ title: "Sign up failed", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Account created! ✅", description: "Welcome to Serviz Hub!" });
      }
    } else {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        toast({ title: "Login failed", description: error.message, variant: "destructive" });
      } else if (data.session) {
        const { data: factorsData } = await supabase.auth.mfa.listFactors();
        if (factorsData && factorsData.totp.length > 0) {
          setMfaFactorId(factorsData.totp[0].id);
          setShowMFAChallenge(true);
          setLoading(false);
          return;
        }
      }
    }

    setLoading(false);
  };

  const handleVerifyMFA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaFactorId || mfaCode.length < 6) return;
    setLoading(true);

    try {
      const challenge = await supabase.auth.mfa.challenge({ factorId: mfaFactorId });
      if (challenge.error) throw challenge.error;

      const verify = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: challenge.data.id,
        code: mfaCode,
      });
      if (verify.error) throw verify.error;

      setShowMFAChallenge(false);
      toast({ title: "Verified! ✅", description: "Two-Factor Auth Successful." });
    } catch (err: unknown) {
      toast({ title: "Verification failed", description: err instanceof Error ? err.message : "Invalid Authenticator code.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  /* ── Shared OTP slot style ── */
  const otpSlotClass = "w-10 h-12 text-foreground rounded-lg";
  const otpSlotStyle = { background: "var(--input-bg)", borderColor: "var(--input-border)", border: "1px solid var(--input-border)" };

  return (
    <div className="h-[100dvh] overflow-y-auto no-scrollbar bg-emerald-base flex flex-col relative">
      <div className="bg-noise" />
      <div className="fluid-bg-container">
        <div className="fluid-blob blob-1" />
        <div className="fluid-blob blob-2" />
        <div className="fluid-blob blob-3" />
      </div>
      <div className="flex-1 w-full flex flex-col items-center pt-[4vh] pb-6 px-6 min-h-max relative z-10">

        <div className="w-full max-w-sm space-y-4">
          {/* Logo & Header */}
          <div className="text-center space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="flex flex-col items-center justify-center gap-1">
              <img
                src="/logo.webp"
                alt="ServizHub Logo"
                className="w-[96px] h-[96px] object-contain drop-shadow-xl"
                fetchPriority="high"
                decoding="async"
              />
              <h1 className="text-3xl font-extrabold text-foreground tracking-tight mt-1">ServizHub</h1>
            </div>
            <div className="space-y-1 text-foreground/90">
              {showMFAChallenge || showOtpInput ? (
                <h2 className="text-2xl tracking-tight font-bold text-foreground">
                  {showMFAChallenge ? "Security" : "Verify"}
                </h2>
              ) : null}
              <p className="text-sm px-2 font-medium leading-[1.6] text-muted-foreground">
                {showMFAChallenge
                  ? "Open your Authenticator app to view your code."
                  : showOtpInput
                    ? `Enter the code we sent to ${phone}`
                    : "Book trusted home service pros in minutes."}
              </p>
            </div>
          </div>

          <div className="glass-frozen rounded-[24px] p-5 sm:p-6 space-y-4 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-100">
            {/* Login Method Toggle */}
            {mode === "login" && !showOtpInput && !showMFAChallenge && (
              <div className="flex border-b border-[var(--glass-border)] pb-4 mb-2">
                <button
                  onClick={() => setLoginMethod("phone")}
                  className={`flex-1 text-xs font-bold uppercase tracking-widest transition-all duration-200 ${loginMethod === "phone" ? "text-emerald-accent border-b-2 border-emerald-accent pb-3 -mb-[18px]" : "text-muted-foreground hover:text-foreground pb-3"}`}
                >
                  Phone
                </button>
                <button
                  onClick={() => setLoginMethod("email")}
                  className={`flex-1 text-xs font-bold uppercase tracking-widest transition-all duration-200 ${loginMethod === "email" ? "text-emerald-accent border-b-2 border-emerald-accent pb-3 -mb-[18px]" : "text-muted-foreground hover:text-foreground pb-3"}`}
                >
                  Email
                </button>
              </div>
            )}

            <div className="space-y-4">
              {showMFAChallenge ? (
                <form onSubmit={handleVerifyMFA} className="space-y-6">
                  <div className="flex justify-center w-full">
                    <InputOTP
                      maxLength={6}
                      value={mfaCode}
                      onChange={(value) => setMfaCode(value.replace(/\D/g, "").slice(0, 6))}
                      autoFocus
                    >
                      <InputOTPGroup className="gap-2">
                        <InputOTPSlot index={0} className={otpSlotClass} style={otpSlotStyle} />
                        <InputOTPSlot index={1} className={otpSlotClass} style={otpSlotStyle} />
                        <InputOTPSlot index={2} className={otpSlotClass} style={otpSlotStyle} />
                        <InputOTPSlot index={3} className={otpSlotClass} style={otpSlotStyle} />
                        <InputOTPSlot index={4} className={otpSlotClass} style={otpSlotStyle} />
                        <InputOTPSlot index={5} className={otpSlotClass} style={otpSlotStyle} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <Button
                    type="submit"
                    disabled={loading || mfaCode.length !== 6}
                    className="w-full h-14 btn-emerald-primary rounded-full transition-all active:scale-[0.98]"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify Device"}
                  </Button>
                </form>
              ) : showOtpInput ? (
                <form onSubmit={handleVerifyOtp} className="space-y-6">
                  <div className="flex justify-center w-full">
                    <InputOTP
                      maxLength={6}
                      value={otp}
                      onChange={(value) => setOtp(value.replace(/\D/g, "").slice(0, 6))}
                      autoFocus
                    >
                      <InputOTPGroup className="gap-2">
                        <InputOTPSlot index={0} className={otpSlotClass} style={otpSlotStyle} />
                        <InputOTPSlot index={1} className={otpSlotClass} style={otpSlotStyle} />
                        <InputOTPSlot index={2} className={otpSlotClass} style={otpSlotStyle} />
                        <InputOTPSlot index={3} className={otpSlotClass} style={otpSlotStyle} />
                        <InputOTPSlot index={4} className={otpSlotClass} style={otpSlotStyle} />
                        <InputOTPSlot index={5} className={otpSlotClass} style={otpSlotStyle} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  <Button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    className="w-full h-14 btn-emerald-primary rounded-full transition-all active:scale-[0.98]"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify OTP"}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {mode === "signup" && (
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">Full Name</label>
                      <Input
                        placeholder="John Doe"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="input-glass rounded-xl h-14 px-4 placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-emerald-accent focus-visible:border-emerald-accent outline-none transition-all"
                        required
                      />
                    </div>
                  )}
                  {loginMethod === "email" || mode === "signup" ? (
                    <>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">Email address</label>
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
                          <Input
                            type="email"
                            placeholder="hello@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="input-glass rounded-xl h-14 pl-11 pr-4 placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-emerald-accent focus-visible:border-emerald-accent outline-none transition-all"
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">Password</label>
                        <div className="relative">
                          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="input-glass rounded-xl h-14 pl-11 pr-12 placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-emerald-accent focus-visible:border-emerald-accent outline-none transition-all"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            className="absolute right-0 top-0 h-full w-12 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </button>
                        </div>
                      </div>

                      {mode === "signup" && (
                        <>
                          {password.length > 0 && (
                            <div className="space-y-1.5 px-1 pb-1">
                              <div className="flex justify-between items-center">
                                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wide">Strength</span>
                                <span className={`text-[10px] font-bold uppercase tracking-wider ${pwdStrength.score <= 1 ? "text-red-500" : pwdStrength.score === 2 ? "text-amber-500" : "text-emerald-accent"
                                  }`}>{pwdStrength.label}</span>
                              </div>
                              <div className="flex gap-1 h-1.5 w-full rounded-full overflow-hidden" style={{ background: "var(--input-bg)" }}>
                                <div className={`h-full transition-all duration-300 ${pwdStrength.score >= 1 ? pwdStrength.color : "bg-transparent"}`} style={{ width: "33.33%" }} />
                                <div className={`h-full transition-all duration-300 ${pwdStrength.score >= 2 ? pwdStrength.color : "bg-transparent"}`} style={{ width: "33.33%" }} />
                                <div className={`h-full transition-all duration-300 ${pwdStrength.score >= 3 ? pwdStrength.color : "bg-transparent"}`} style={{ width: "33.33%" }} />
                              </div>
                            </div>
                          )}

                          <div className="space-y-2 pt-1">
                            <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">Confirm Password</label>
                            <div className="relative">
                              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
                              <Input
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="••••••••"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className={`input-glass rounded-xl h-14 pl-11 pr-12 placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-emerald-accent outline-none transition-all ${confirmPassword && password !== confirmPassword ? "border-red-400 focus-visible:border-red-400" : ""
                                  }`}
                                required
                              />
                              <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                aria-label={showConfirmPassword ? "Hide confirmation password" : "Show confirmation password"}
                                className="absolute right-0 top-0 h-full w-12 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                              >
                                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">Phone Number</label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
                        <Input
                          type="tel"
                          placeholder="09XX XXX XXXX"
                          value={phone}
                          onChange={handlePhoneChange}
                          className="input-glass rounded-xl h-14 pl-11 pr-4 placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-emerald-accent focus-visible:border-emerald-accent outline-none transition-all"
                          required
                        />
                      </div>
                    </div>
                  )}

                  <div className="pt-4">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full h-14 btn-emerald-primary rounded-full transition-all active:scale-[0.98]"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : mode === "login" ? "Log in" : "Create Account"}
                    </Button>
                  </div>
                </form>
              )}

              {/* Social Login */}
              {!showOtpInput && !showMFAChallenge && (
                <div className="space-y-4 pt-4">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t" style={{ borderColor: "var(--glass-border)" }} />
                    </div>
                    <div className="relative flex justify-center text-[11px] uppercase tracking-wider font-bold">
                      <span className="bg-transparent px-4 text-muted-foreground rounded-full">
                        Or Continue With Account
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-center gap-4">
                    {/* Google Icon */}
                    <button
                      aria-label="Continue with Google"
                      className="w-12 h-12 rounded-full flex items-center justify-center transition-colors hover:bg-muted/10"
                      style={{ background: "var(--input-bg)", border: "1px solid var(--glass-border)" }}
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                    </button>
                    {/* Apple Icon */}
                    <button
                      aria-label="Continue with Apple"
                      className="w-12 h-12 rounded-full flex items-center justify-center transition-colors text-foreground hover:bg-muted/10"
                      style={{ background: "var(--input-bg)", border: "1px solid var(--glass-border)" }}
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 18.57c-1.32.06-2.83-.81-4.04-.81-1.22 0-3.08.88-4.14.88-1.12.06-2.58-.37-3.83-1.62-2.5-2.62-3.83-7.56-1.56-11.25.99-1.56 2.5-2.62 4.14-2.62 1.25 0 2.81.81 3.56.81.81 0 2.56-1.06 4.25-1.06 2.06 0 3.87.94 4.81 2.5-.06 0-3.12 1.62-3.12 4.69 0 3.56 3.12 4.69 3.12 4.69-.06.12-1.56 4.31-4.37 4.31.06-.06.75-.43 1.18-.52zm-3.06-15.56c.75-.81 1.25-2.06 1.12-3.12-1 .06-2.25.69-3.06 1.56-.69.69-1.25 1.88-1.12 3.06 1.06.06 2.19-.62 3.06-1.5z" />
                      </svg>
                    </button>
                  </div>

                  {/* Toggle Link */}
                  <div className="text-center pt-2">
                    <p className="text-muted-foreground text-[13px] font-medium">
                      {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
                      <button
                        type="button"
                        onClick={() => setMode(mode === "login" ? "signup" : "login")}
                        className="text-emerald-accent font-bold hover:underline underline-offset-4 ml-1"
                      >
                        {mode === "login" ? "Sign Up here" : "Log in"}
                      </button>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;
